import React, { useState, useEffect } from 'react';
import { FaFolder, FaFolderOpen, FaFileAlt, FaArrowRight, FaEdit, FaTrash } from 'react-icons/fa';
import { getFileTypeIcon } from '../../utils/formatters';
import '../../styles/Files.css';

const FolderStructure = ({ files, onSelectFile, onMoveFile, onCreateFolder, onDeleteFolder, onRenameFolder }) => {
  const [folders, setFolders] = useState({
    'root': { name: 'All Files', expanded: true, parent: null }
  });
  const [activeFolderId, setActiveFolderId] = useState('root');
  const [editingFolder, setEditingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);

  // Generate AI-sorted folders initially
  useEffect(() => {
    // In a real implementation, this would call the backend AI service
    // For now, we'll use a simple dummy implementation
    const categorizedFolders = {
      'root': { name: 'All Files', expanded: true, parent: null },
      'folder-1': { name: 'Characters', expanded: false, parent: 'root' },
      'folder-2': { name: 'Terrain', expanded: false, parent: 'root' },
      'folder-3': { name: 'Props', expanded: false, parent: 'root' },
      'folder-4': { name: 'Fantasy', expanded: false, parent: 'folder-1' },
      'folder-5': { name: 'Sci-Fi', expanded: false, parent: 'folder-1' }
    };
    
    setFolders(categorizedFolders);
  }, []);

  const toggleFolder = (folderId) => {
    setFolders(prev => ({
      ...prev,
      [folderId]: {
        ...prev[folderId],
        expanded: !prev[folderId].expanded
      }
    }));
  };

  const selectFolder = (folderId) => {
    setActiveFolderId(folderId);
  };

  const handleCreateFolder = () => {
    const newFolderId = `folder-${Date.now()}`;
    const newFolder = {
      name: 'New Folder',
      expanded: false,
      parent: activeFolderId
    };
    
    setFolders(prev => ({
      ...prev,
      [newFolderId]: newFolder
    }));
    
    setEditingFolder(newFolderId);
    setNewFolderName('New Folder');
    
    // Call the provided callback if available
    if (onCreateFolder) {
      onCreateFolder(newFolderId, newFolder.name, activeFolderId);
    }
  };

  const handleRenameFolder = (folderId) => {
    setEditingFolder(folderId);
    setNewFolderName(folders[folderId].name);
  };

  const saveRename = () => {
    if (!newFolderName.trim()) return;
    
    setFolders(prev => ({
      ...prev,
      [editingFolder]: {
        ...prev[editingFolder],
        name: newFolderName
      }
    }));
    
    // Call the provided callback if available
    if (onRenameFolder) {
      onRenameFolder(editingFolder, newFolderName);
    }
    
    setEditingFolder(null);
  };

  const handleDeleteFolder = (folderId) => {
    if (window.confirm(`Are you sure you want to delete the folder "${folders[folderId].name}"?`)) {
      // Find all child folders
      const childFolderIds = Object.keys(folders).filter(
        id => folders[id].parent === folderId
      );
      
      // Recursively delete child folders
      const deleteFolder = (id) => {
        const childIds = Object.keys(folders).filter(
          childId => folders[childId].parent === id
        );
        
        childIds.forEach(deleteFolder);
        
        setFolders(prev => {
          const { [id]: _, ...remaining } = prev;
          return remaining;
        });
      };
      
      // Delete the target folder and all its children
      childFolderIds.forEach(deleteFolder);
      
      setFolders(prev => {
        const { [folderId]: _, ...remaining } = prev;
        return remaining;
      });
      
      // If the active folder is being deleted, select its parent
      if (activeFolderId === folderId) {
        setActiveFolderId(folders[folderId].parent || 'root');
      }
      
      // Call the provided callback if available
      if (onDeleteFolder) {
        onDeleteFolder(folderId);
      }
    }
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    if (draggedItem && draggedItem.type === 'file' && folderId !== activeFolderId) {
      e.currentTarget.classList.add('drag-over');
    }
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e, targetFolderId) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    if (draggedItem && draggedItem.type === 'file' && targetFolderId !== activeFolderId) {
      // Call the provided callback if available
      if (onMoveFile) {
        onMoveFile(draggedItem.id, activeFolderId, targetFolderId);
      }
    }
    
    setDraggedItem(null);
  };

  // Render folder structure recursively
  const renderFolders = (parentId) => {
    const childFolders = Object.keys(folders)
      .filter(id => folders[id].parent === parentId)
      .sort((a, b) => folders[a].name.localeCompare(folders[b].name));
    
    if (childFolders.length === 0) {
      return null;
    }
    
    return (
      <ul className="folder-list">
        {childFolders.map(folderId => (
          <li key={folderId} className={activeFolderId === folderId ? 'active' : ''}>
            <div 
              className="folder-item"
              onClick={() => selectFolder(folderId)}
              onDragOver={(e) => handleDragOver(e, folderId)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, folderId)}
            >
              <span 
                className="folder-toggle" 
                onClick={(e) => { e.stopPropagation(); toggleFolder(folderId); }}
              >
                {folders[folderId].expanded ? <FaFolderOpen /> : <FaFolder />}
              </span>
              
              {editingFolder === folderId ? (
                <div className="folder-rename">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveRename()}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <button onClick={(e) => { e.stopPropagation(); saveRename(); }}>
                    <FaArrowRight />
                  </button>
                </div>
              ) : (
                <>
                  <span className="folder-name">{folders[folderId].name}</span>
                  <div className="folder-actions">
                    <button
                      className="folder-action-btn"
                      onClick={(e) => { e.stopPropagation(); handleRenameFolder(folderId); }}
                    >
                      <FaEdit />
                    </button>
                    {folderId !== 'root' && (
                      <button
                        className="folder-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folderId); }}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {folders[folderId].expanded && renderFolders(folderId)}
          </li>
        ))}
      </ul>
    );
  };

  // Get files for the currently selected folder
  const getFilesForFolder = () => {
    // In a real implementation, files would be stored with folder associations
    // For this dummy implementation, we'll distribute files among folders
    
    if (!files || files.length === 0) {
      return [];
    }
    
    // Dummy folder distribution
    const folderAssignments = {
      'root': files,
      'folder-1': files.filter((_, index) => index % 5 === 0),
      'folder-2': files.filter((_, index) => index % 5 === 1),
      'folder-3': files.filter((_, index) => index % 5 === 2),
      'folder-4': files.filter((_, index) => index % 5 === 3),
      'folder-5': files.filter((_, index) => index % 5 === 4)
    };
    
    return folderAssignments[activeFolderId] || [];
  };

  return (
    <div className="folder-structure">
      <div className="folder-sidebar">
        <div className="folder-actions-header">
          <button 
            className="create-folder-btn"
            onClick={handleCreateFolder}
          >
            New Folder
          </button>
        </div>
        
        {renderFolders(null)}
      </div>
      
      <div className="folder-content">
        <h3>{folders[activeFolderId]?.name || 'All Files'}</h3>
        
        <div className="folder-files">
          {getFilesForFolder().map(file => (
            <div
              key={file.originalId}
              className="folder-file-item"
              onClick={() => onSelectFile && onSelectFile(file)}
              draggable
              onDragStart={(e) => handleDragStart(e, { type: 'file', id: file.originalId })}
            >
              <div className="file-type-icon">{getFileTypeIcon(file.name)}</div>
              <span className="file-name">{file.name}</span>
            </div>
          ))}
          
          {getFilesForFolder().length === 0 && (
            <div className="empty-folder">
              <FaFileAlt />
              <p>No files in this folder</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderStructure;